import h from 'hyperscript';

import { createButton } from 'ui/button/button';
import { Alerts } from 'ui/alerts/alerts';

import {
  type AnimationSettingResult,
  AnimationSettingsDialog,
  type View
} from 'cadenza/conditions/actions/animation-settings-dialog';
import type { StepsConfig } from 'cadenza/conditions/date-time-condition/steps-config';
import { DateAnimationPreview } from 'cadenza/conditions/date-time-condition/date-animation-preview';
import { type TickUnit, unitLabel } from 'cadenza/conditions/date-time-condition/tick-unit';
import {
  SliderStepsConfig,
  STEPS_CONFIG_CHANGE
} from 'cadenza/conditions/date-time-condition/slider-steps-config';
import {
  type AutomaticFrameDistributionResult,
  calculateAutomaticFrameDistribution
} from 'cadenza/conditions/animation/automatic-frame-distribution-utils';
import {
  DATE_RANGE_CHANGE,
  DateRangePicker
} from 'cadenza/conditions/date-range-picker/date-range-picker';
import { assert } from 'cadenza/utils/custom-error';

import i18n from './date-animation-settings-dialog.properties';
import './date-animation-settings-dialog.css';

const COMPONENT_NAME = 'd-date-animation-settings-dialog';

export class DateAnimationSettingsDialog extends AnimationSettingsDialog {

  _initialStepsConfig: StepsConfig;
  _dateFormat: string;
  _smallestUnit: TickUnit;
  _defaultStart: Date;
  _customStart: Date;
  _defaultEnd: Date;
  _customEnd: Date;
  _maxFrames: number;
  _pointInTimeMode: boolean;

  _animationRangePicker: DateRangePicker;
  _preview: DateAnimationPreview;
  _stepsConfigPanel?: SliderStepsConfig;
  _alerts: Alerts;
  _automaticDistributionAlerts: Alerts;

  constructor (
    affectedViews: View[],
    initialStepsConfig: StepsConfig,
    dateFormat: string,
    smallestUnit: TickUnit,
    defaultStart: Date,
    customStart: Date,
    defaultEnd: Date,
    customEnd: Date,
    maxFrames: number,
    pointInTimeMode = false) {
    super(affectedViews);
    this._initialStepsConfig = initialStepsConfig;
    this._dateFormat = dateFormat;
    this._smallestUnit = smallestUnit;
    this._defaultStart = defaultStart;
    this._customStart = customStart;
    this._defaultEnd = defaultEnd;
    this._customEnd = customEnd;
    this._maxFrames = maxFrames;
    this._pointInTimeMode = pointInTimeMode;

    this._animationRangePicker = this._createAndListenToRangePicker();
    this._preview = this._createPreview(this._initialStepsConfig);
    if (!pointInTimeMode) {
      this._stepsConfigPanel = this._createAndListenToStepsConfigPanel(initialStepsConfig);
    }
    this._alerts = this._createAlerts();
    this._automaticDistributionAlerts = this._createAlerts();
    this._updateAlerts();
  }

  createSpecificContent (): HTMLElement[] {
    if (this._pointInTimeMode) {
      return [
        this._preview,
        this._alerts
      ];
    } else {
      return [
        this._createAutomaticDistribution(),
        this._preview,
        this._stepsConfigPanel!, // there is always a _stepConfigPanel if we are not in point-in-time mode
        this._alerts
      ];
    }
  }

  _createAndListenToRangePicker () {
    const rangePicker = new DateRangePicker({
      initialStartDate: this._customStart,
      initialEndDate: this._customEnd,
      minDate: this._defaultStart,
      maxDate: this._defaultEnd,
      dateFormat: this._dateFormat,
      mostFineGrainedTickUnit: this._smallestUnit,
      popupMode: true
    });
    rangePicker.classList.add('animation-visualization--range-picker');
    rangePicker.addEventListener(DATE_RANGE_CHANGE, (event) => {
      assert(event instanceof CustomEvent, 'event must be an instance of CustomEvent.');
      let effectiveValue;
      if (event.detail.isStart) {
        effectiveValue = event.detail.value ?? this._defaultStart;
        this._customStart = effectiveValue;
      } else {
        effectiveValue = event.detail.value ?? this._defaultEnd;
        this._customEnd = effectiveValue;
      }
      this._stepsConfigPanel?.updateDateRange(event.detail.isStart, effectiveValue);
      this._updatePreview();
    });
    return rangePicker;
  }

  _createPreview (stepsConfig: StepsConfig): DateAnimationPreview {
    return new DateAnimationPreview(
      stepsConfig,
      this._smallestUnit,
      this._effectiveStart(),
      this._effectiveEnd(),
      this._maxFrames,
      this._animationRangePicker);
  }

  _createAndListenToStepsConfigPanel (initialStepsConfig: StepsConfig): SliderStepsConfig {
    const stepsConfigPanel = new SliderStepsConfig(
      initialStepsConfig,
      this._smallestUnit,
      this._effectiveStart(),
      this._effectiveEnd()
    );
    stepsConfigPanel.addEventListener(STEPS_CONFIG_CHANGE, () => this._updatePreview());
    return stepsConfigPanel;
  }

  _createAlerts () {
    const alerts = new Alerts();
    alerts.classList.add('d-date-animation-settings-dialog--alerts');
    alerts.compactMessages = true;
    return alerts;
  }

  _updateAlerts () {
    this._alerts.alerts = [];
    const stepsConfig = this._currentStepsConfig();

    if (this._preview?.hasLessThanTwoFrames()) {
      this._alerts.alerts = [ {
        title: '',
        message: i18n('alert.onlyOneFrame'),
        type: 'warning'
      } ];
    } else if (this._preview?.hasOverset()) {
      this._alerts.alerts = [ {
        title: '',
        message: i18n(
          'alert.overset',
          {
            window: stepsConfig.timeWindow + ' '
                + unitLabel(stepsConfig.unit, stepsConfig.timeWindow !== 1)
          }),
        type: 'warning'
      } ];
    } else {
      this._alerts.alerts = [];
    }
  }

  _updateAutomaticFrameDistributionAlerts (message: string | undefined) {
    this._automaticDistributionAlerts.alerts = message
      ? [ { title: '', message, type: 'warning' } ]
      : [];
  }

  _effectiveStart () {
    return this._customStart ?? this._defaultStart;
  }

  _effectiveEnd () {
    return this._customEnd ?? this._defaultEnd;
  }

  _updatePreview (frameDistributionResult?: AutomaticFrameDistributionResult) {
    if (this._preview) {
      const updatedPreview = this._createPreview(this._currentStepsConfig());
      this._preview.replaceWith(updatedPreview);
      this._preview = updatedPreview;
      this._updateAlerts();
    }
  }

  _calculateAutomaticFrameDistribution () {
    const result = calculateAutomaticFrameDistribution(
      this._maxFrames,
      this._effectiveStart(),
      this._effectiveEnd(),
      this._smallestUnit);
    this._stepsConfigPanel?.setStepsConfig(result.stepsConfig);
    this._updatePreview();
    this._updateAutomaticFrameDistributionAlerts(result?.message);
  }

  _createAutomaticDistribution () {
    return h('.form-group', [
      h('label', i18n('settingsSubheading')),
      h('p', i18n('animation.settings.automatic-distribution')),
      createButton(i18n('animation.settings.generate.automatic-distribution'), {
        onclick: () => this._calculateAutomaticFrameDistribution()
      }),
      this._automaticDistributionAlerts
    ]);
  }

  _currentStepsConfig () {
    return this._stepsConfigPanel?.getStepsConfig() ?? this._initialStepsConfig;
  }

  onSubmit (event: SubmitEvent) {
    const result: AnimationSettingResult = {
      type: 'date',
      viewId: this._viewSelect?.selection[0].id,
      ...this._currentStepsConfig(),
      rangeStart: this._effectiveStart().toISOString(),
      rangeEnd: this._effectiveEnd().toISOString(),
      sliderRangeStart: this._effectiveStart(),
      sliderRangeEnd: this._effectiveEnd()
    };
    this.close(result);
  }

}

customElements.define(COMPONENT_NAME, DateAnimationSettingsDialog);
